"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useBeamContext = exports.BeamProvider = exports.BeamContext = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const form_state_1 = require("@homebound/form-state");
const react_1 = require("react");
const react_aria_1 = require("react-aria");
const Modal_1 = require("./Modal/Modal");
const PresentationContext_1 = require("./PresentationContext");
const SnackbarContext_1 = require("./Snackbar/SnackbarContext");
const SuperDrawer_1 = require("./SuperDrawer/SuperDrawer");
const index_1 = require("../utils/index");
const Layout_1 = require("./Layout");
const ToastContext_1 = require("./Toast/ToastContext");
/** This is only exported internally, for useModal and useSuperDrawer, it's not a public API. */
exports.BeamContext = (0, react_1.createContext)({
    modalState: new index_1.EmptyRef(),
    modalCanCloseChecks: new index_1.EmptyRef(),
    modalHeaderDiv: undefined,
    modalBodyDiv: undefined,
    modalFooterDiv: undefined,
    drawerContentStack: new index_1.EmptyRef(),
    drawerCanCloseChecks: new index_1.EmptyRef(),
    drawerCanCloseDetailsChecks: new index_1.EmptyRef(),
    sdHeaderDiv: undefined,
});
function BeamProvider({ children, ...presentationProps }) {
    // We want the identity of these to be stable, b/c they end up being used as dependencies
    // in both useModal's and useSuperDrawer's return values, which means the end-application's
    // dependencies as well, i.e. things like GridTable rowStyles will memoize on openInDrawer.
    // So we use refs + a tick.
    const [, tick] = (0, react_1.useReducer)((prev) => prev + 1, 0);
    const modalRef = (0, react_1.useRef)();
    const modalHeaderDiv = (0, react_1.useMemo)(() => document.createElement("div"), []);
    const modalBodyDiv = (0, react_1.useMemo)(() => {
        const el = document.createElement("div");
        // Ensure this wrapping div takes up the full height of its container in the case of a virtualized table within.
        el.style.height = "100%";
        return el;
    }, []);
    const modalCanCloseChecksRef = (0, react_1.useRef)([]);
    const modalFooterDiv = (0, react_1.useMemo)(() => document.createElement("div"), []);
    const drawerContentStackRef = (0, react_1.useRef)([]);
    const drawerCanCloseChecks = (0, react_1.useRef)([]);
    const drawerCanCloseDetailsChecks = (0, react_1.useRef)([]);
    const sdHeaderDiv = (0, react_1.useMemo)(() => document.createElement("div"), []);
    // We essentially expose the refs, but with our own getters/setters so that we can
    // have the setters call `tick` to re-render this Provider
    const context = (0, react_1.useMemo)(() => {
        return {
            // These two keys need to trigger re-renders on change
            modalState: new PretendRefThatTicks(modalRef, tick),
            drawerContentStack: new PretendRefThatTicks(drawerContentStackRef, tick),
            // The rest we don't need to re-render when these are mutated, so just expose as-is
            modalCanCloseChecks: modalCanCloseChecksRef,
            modalHeaderDiv,
            modalBodyDiv,
            modalFooterDiv,
            drawerCanCloseChecks,
            drawerCanCloseDetailsChecks,
            sdHeaderDiv,
        };
    }, [modalBodyDiv, modalFooterDiv]);
    return ((0, jsx_runtime_1.jsx)(exports.BeamContext.Provider, { value: { ...context }, children: (0, jsx_runtime_1.jsx)(PresentationContext_1.PresentationProvider, { ...presentationProps, children: (0, jsx_runtime_1.jsx)(Layout_1.RightPaneProvider, { children: (0, jsx_runtime_1.jsx)(form_state_1.AutoSaveStatusProvider, { children: (0, jsx_runtime_1.jsx)(SnackbarContext_1.SnackbarProvider, { children: (0, jsx_runtime_1.jsxs)(ToastContext_1.ToastProvider, { children: [(0, jsx_runtime_1.jsxs)(react_aria_1.OverlayProvider, { children: [children, modalRef.current && (0, jsx_runtime_1.jsx)(Modal_1.Modal, { ...modalRef.current })] }), (0, jsx_runtime_1.jsx)(SuperDrawer_1.SuperDrawer, {})] }) }) }) }) }) }));
}
exports.BeamProvider = BeamProvider;
/** Looks like a ref, but invokes a re-render on set (w/o changing the setter identity). */
class PretendRefThatTicks {
    constructor(ref, tick) {
        this.ref = ref;
        this.tick = tick;
    }
    get current() {
        return this.ref.current;
    }
    set current(value) {
        this.ref.current = value;
        this.tick();
    }
}
function useBeamContext() {
    return (0, react_1.useContext)(exports.BeamContext);
}
exports.useBeamContext = useBeamContext;
